import AppLoader from "../GeneralComponents/AppLoader";
import { Tooltip } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useSnackbar } from "notistack";
import { MAX_RECORDING_MINUTES } from "../../utils/classRoomUtils";
let micError =
  "Microphone access is denied. Please grant permission in your browser settings.";

const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "webm" : "mp4";

export default function VoiceRecorder(props) {
  const { handleSubmit, isSubmitting, setIsSubmitting, setIsStartRecording } =
    props;
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timer, setTimer] = useState(0);
  const [myStream, setMyStream] = useState(null);
  const [recordedData, setRecordedData] = useState([]);
  const mediaRecorderRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const startRecording = async () => {
    try {
      const mic = await navigator.permissions.query({ name: "microphone" });
      if (mic.state === "denied") {
        enqueueSnackbar(micError, { variant: "error" });
        setIsStartRecording(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      setMyStream(stream);
      setIsRecording(true);
      setIsPaused(false);
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: `audio/${mimeType}`,
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          setRecordedData((old) => [...old, event.data]);
        }
      };

      mediaRecorder.start(100);
      stream.getTracks().forEach((track) => {
        track.onended = () => mediaRecorder.stop();
      });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
      setIsStartRecording(false);
      return;
    }
  };

  const pauseRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr?.state === "recording") {
      mr.pause();
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr?.state === "paused") {
      mr.resume();
      setIsPaused(false);
    }
  };

  const handleCancelRecording = () => {
    if (myStream) {
      myStream.getTracks().forEach((track) => track.stop());
    }
    setIsStartRecording(false);
  };

  const stopRecording = () => {
    setIsSubmitting(true);
    const mergedBlob = new Blob(recordedData, {
      type: mimeType,
    });
    handleSubmit({ file: mergedBlob, mimeType, duration: timer });
    if (myStream) {
      myStream.getTracks().forEach((track) => track.stop());
    }
  };

  const handleCompleted = () => {
    setIsPaused(true);
    setIsRecording(false);
    if (myStream) {
      myStream.getTracks().forEach((track) => track.stop());
    }
  };

  const handleStoped = () => {
    mediaRecorderRef.current = null;
    if (myStream) {
      myStream.getTracks().forEach((track) => track.stop());
    }
    setIsRecording(false);
    setIsPaused(false);
    setTimer(0);
    setIsStartRecording(false);
  };

  useEffect(() => {
    startRecording();
    return () => handleStoped();
  }, []);

  useEffect(() => {
    let interval;
    if (isRecording && !isPaused) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isRecording, isPaused]);

  useEffect(() => {
    if (timer >= MAX_RECORDING_MINUTES * 60) {
      handleCompleted();
    }
  }, [timer]);

  let minutes = Math.floor(timer / 60);
  let seconds = timer % 60;

  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }

  const recordedPercent = Math.min(
    100,
    (timer / (MAX_RECORDING_MINUTES * 60)) * 100
  );

  return (
    <div className="voice-recorder">
      <div className="voice-recorder-bar">
        <div className="voice-recorder-controls">
          <Tooltip title="Cancel recording">
            <button
              type="button"
              className="voice-recorder-btn voice-recorder-btn--cancel"
              onClick={handleCancelRecording}
              aria-label="Cancel recording"
            >
              <ClearIcon />
            </button>
          </Tooltip>
          {isRecording && (
            <Tooltip title={isPaused ? "Resume" : "Pause"}>
              <button
                type="button"
                className="voice-recorder-btn voice-recorder-btn--pause-play"
                onClick={isPaused ? resumeRecording : pauseRecording}
                aria-label={isPaused ? "Resume recording" : "Pause recording"}
              >
                {isPaused ? (
                  <PlayArrowIcon fontSize="small" />
                ) : (
                  <PauseIcon fontSize="small" />
                )}
              </button>
            </Tooltip>
          )}
        </div>
        <div className="voice-recorder-progress-wrap">
          <div
            className={`voice-recorder-dot ${isPaused ? "voice-recorder-dot--paused" : ""}`}
            aria-hidden
          />
          <div className="voice-recorder-track">
            <div
              className="voice-recorder-fill"
              style={{ width: `${recordedPercent}%` }}
            />
          </div>
        </div>
        <div className="voice-recorder-meta">
          {isPaused && (
            <span className="voice-recorder-status" aria-label="Paused">
              Paused
            </span>
          )}
          <span className="voice-recorder-time" aria-live="polite">
            {minutes}:{seconds}
          </span>
        </div>
        <div className="voice-recorder-send">
          {isSubmitting ? (
            <span className="voice-recorder-btn voice-recorder-btn--send" aria-busy="true">
              <AppLoader pixelSize={22} />
            </span>
          ) : (
            <Tooltip title="Send voice message">
              <button
                type="button"
                className="voice-recorder-btn voice-recorder-btn--send"
                onClick={stopRecording}
                aria-label="Send voice message"
              >
                <SendIcon fontSize="small" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
